import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";
import { Skeleton } from "@/shared/ui";
import type { CashFlowChartPoint } from "../../../types/api";
import { formatCompact, formatCurrencyNoDecimals } from "../../../utils/format";
import ChartLegend from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

interface Props {
  data: CashFlowChartPoint[];
  isLoading: boolean;
}

const COLOR_INFLOWS = "#11BF68";
const COLOR_OUTFLOWS = "#94A3B8";

function absFormat(v: number) {
  return formatCurrencyNoDecimals(Math.abs(v));
}

export default function CashFlowChart({ data, isLoading }: Props) {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = i18n.language || "ru";

  if (isLoading) {
    return <Skeleton height={240} className="radius-sm" />;
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[240px] text-body-regular-sm content-base-secondary">
        {t("page.noData")}
      </div>
    );
  }

  function tickFormatter(value: number) {
    return formatCompact(value, locale);
  }

  return (
    <div style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }} barSize={32} barGap={-32}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={tickFormatter}
          />
          <ReferenceLine y={0} stroke="var(--chart-ref-line)" />
          <Tooltip content={<ChartTooltip formatValue={absFormat} />} cursor={{ fill: "transparent" }} />
          <Legend content={<ChartLegend />} />
          <Bar
            dataKey="inflows"
            name={t("cashFlow.inflows")}
            fill={COLOR_INFLOWS}
            fillOpacity={0.85}
            activeBar={{ fillOpacity: 1 }}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="outflows"
            name={t("cashFlow.outflows")}
            fill={COLOR_OUTFLOWS}
            fillOpacity={0.85}
            activeBar={{ fillOpacity: 1 }}
            radius={[4, 4, 0, 0]}
          />
          <Line
            dataKey="netChange"
            name={t("cashFlow.netChange")}
            type="monotone"
            stroke="var(--chart-line)"
            strokeWidth={2}
            dot={{ r: 3, fill: "var(--chart-line)", strokeWidth: 0 }}
            activeDot={{ r: 4, fill: "var(--chart-line)", strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
